// React-facing surface for tenant-tz–aware formatters.
// Pure helpers live in `./format-core.js` so Node-based smoke tests can import
// them without touching the React render tree.
//
// Resolution order for `timeZone` when using `useFormatters()`:
//   1. explicit arg at call site (e.g. `formatTime(iso, { timeZone: "UTC" })`)
//   2. AuthContext currentBranch.timezone
//   3. (currentUser.tenant_timezone — reserved for future use)
//   4. hard-coded final fallback `Asia/Kolkata` — NEVER fall back to OS clock.

import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
    FALLBACK_TIMEZONE,
    formatMoney,
    formatDate,
    formatDateTime,
    formatTime,
    formatRelativeTime,
    formatDuration,
    localeForLang,
    getTzParts,
    tzDayKey,
    tzMinutesIntoDay,
    tzWallClockToUtc,
} from "./format-core";

// Re-export pure helpers so existing imports keep working unchanged.
export {
    FALLBACK_TIMEZONE,
    formatMoney,
    formatDate,
    formatDateTime,
    formatTime,
    formatRelativeTime,
    formatDuration,
    localeForLang,
    getTzParts,
    tzDayKey,
    tzMinutesIntoDay,
    tzWallClockToUtc,
};

// Permission helper (kept here for historical co-location).
export const hasPermission = (permissions, perm) =>
    Array.isArray(permissions) && permissions.includes(perm);

/** Resolve the tenant timezone from auth context with hard fallback. */
export function useTenantTz() {
    const { branches, currentBranchId } = useAuth();
    const branch = (branches || []).find((b) => b.id === currentBranchId);
    return (branch && branch.timezone) || FALLBACK_TIMEZONE;
}

/**
 * Memoised formatter bundle pre-bound to the current tenant tz.
 * Components import this instead of the raw functions so timezone consistency
 * is enforced by the React lifecycle, not by every dev remembering to pass tz.
 */
export function useFormatters() {
    const tz = useTenantTz();
    return useMemo(() => {
        const bound = { timeZone: tz };
        return {
            timeZone: tz,
            formatDate: (v, extra = {}) => formatDate(v, { ...bound, ...extra }),
            formatDateTime: (v, extra = {}) => formatDateTime(v, { ...bound, ...extra }),
            formatTime: (v, extra = {}) => formatTime(v, { ...bound, ...extra }),
            formatRelativeTime: (v, extra = {}) => formatRelativeTime(v, { ...bound, ...extra }),
            formatDuration,
            formatMoney,
            getTzParts: (v) => getTzParts(v, tz),
            tzDayKey: (v) => tzDayKey(v, tz),
            tzMinutesIntoDay: (v, dk) => tzMinutesIntoDay(v, dk, tz),
            tzWallClockToUtc: (yy, mm, dd, h, m) => tzWallClockToUtc(yy, mm, dd, h, m, tz),
        };
    }, [tz]);
}
